import { logger } from "@origin-digital/reporting-client";
import type {
  AttachUnauthInternetChatOptions,
  UnauthInternetChatFailure,
  UnauthInternetChatAttachment,
} from "./index";
import {
  completeInternetCheckoutHandoff,
  isInternetCheckoutHandoff,
} from "./internetCheckoutHandoff";
import { loadUnauthInternetChatLauncher } from "./loadUnauthInternetChatLauncher";

import { startUnauthInternetChatAuthGate } from "./unauthInternetChatAuthGate";

export const UNAUTH_INTERNET_CHAT_PATH = "/internet/plans/";

export interface InitializeUnauthInternetChatOptions {
  pathname: string;
  isSSR: boolean;
  isNative: boolean;
}

export function isUnauthInternetChatPath(pathname: string): boolean {
  return pathname === UNAUTH_INTERNET_CHAT_PATH;
}

const reportCheckoutHandoffRejection = (): void => {
  try {
    logger
      .error("[tal-client] unauth Internet checkout handoff rejected", {
        pathname: UNAUTH_INTERNET_CHAT_PATH,
      })
      .catch(() => undefined);
  } catch {
    // Reporting failure must remain isolated from the Internet Plans page.
  }
};

const navigateToCheckout: AttachUnauthInternetChatOptions["onCheckout"] = (
  handoff
) => {
  if (!isInternetCheckoutHandoff(handoff)) {
    reportCheckoutHandoffRejection();
    return;
  }

  completeInternetCheckoutHandoff(handoff);
};

const reportAttachmentFailure = ({
  phase,
}: UnauthInternetChatFailure): void => {
  try {
    logger
      .error("[tal-client] unauth Internet chat attachment failed", {
        phase,
        pathname: UNAUTH_INTERNET_CHAT_PATH,
      })
      .catch(() => undefined);
  } catch {
    // Reporting failure must remain isolated from the Internet Plans page.
  }
};

function importAndAttachUnauthInternetChat(
  canAttach: () => boolean = () => true
): Promise<UnauthInternetChatAttachment | undefined> {
  return loadUnauthInternetChatLauncher().then(
    ({ attachUnauthInternetChat }) => {
      if (!canAttach()) return undefined;
      return attachUnauthInternetChat({
        onCheckout: navigateToCheckout,
        onFailure: reportAttachmentFailure,
        proactive: {
          messages: {
            desktop:
              "Comparing our internet plans? I can help you find the right fit for you — just ask.",
            mobile: "Comparing plans? Let me help!",
          },
          scrollDepthRatio: 0.5,
          targetSelector:
            '[data-id="plans-section"] [data-id="bb-plan-pricing-heading"]',
        },
      }).catch(() => undefined);
    },
    () => {
      reportAttachmentFailure({ phase: "load" });
      return undefined;
    }
  );
}

interface PageIntegration {
  owners: number;
  dispose(): Promise<void>;
}

let pageIntegration: PageIntegration | undefined;
let pendingDisposal = Promise.resolve();

function createPageIntegration(
  previousDisposal: Promise<void>
): PageIntegration {
  let disposed = false;
  let stopAuthGate: (() => Promise<void>) | undefined;
  const start = (): void => {
    // The low-level attachment shares pending handles. Drain the previous owner
    // before a new gate can adopt a handle that the old gate will detach.
    previousDisposal.then(() => {
      if (!disposed) {
        stopAuthGate = startUnauthInternetChatAuthGate(
          importAndAttachUnauthInternetChat
        );
      }
    });
  };
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", start, { once: true });
  } else {
    start();
  }

  return {
    owners: 0,
    dispose: () => {
      disposed = true;
      document.removeEventListener("DOMContentLoaded", start);
      return stopAuthGate ? stopAuthGate() : previousDisposal;
    },
  };
}

/**
 * Selects the public browser journey before crossing either lazy boundary.
 * Eligible callers share one page integration. Each receives an idempotent
 * release; the final release disposes authentication checks and the attachment.
 */
export function initializeUnauthInternetChat({
  pathname,
  isSSR,
  isNative,
}: InitializeUnauthInternetChatOptions): (() => void) | undefined {
  if (
    !isUnauthInternetChatPath(pathname) ||
    isSSR ||
    isNative ||
    typeof document === "undefined"
  ) {
    return;
  }

  const integration =
    pageIntegration ??
    (pageIntegration = createPageIntegration(pendingDisposal));
  integration.owners += 1;
  let released = false;
  return () => {
    if (released) return;
    released = true;
    integration.owners -= 1;
    if (integration.owners === 0) {
      pageIntegration = undefined;
      pendingDisposal = integration.dispose();
    }
  };
}
